import * as XLSX from 'xlsx'
import * as mz from 'moment-timezone'
import moment from 'moment-timezone'
import * as _ from 'lodash'
import {allLocales} from '../../../../Components/Views/Admin/EditSite'
import {UserLanguage} from '../../../../state/rest'

export type SiteDetailExportRow = {
  locationName: string
  siteName: string
  siteId: string
  aromiIntegrationOn: 'Y' | 'N'
}

export const exportSiteDetailsExcel = (
  sites: SiteDetailExportRow[],
  t: (key: string, defaultValue: string) => string
): void => {
  const locationNameHeader = t('locations:export.locationName', 'Location Name')
  const siteNameHeader = t('locations:export.siteName', 'Site Name')
  const siteIdHeader = t('locations:export.siteId', 'Site ID')
  const aromiIntegrationHeader = t('locations:export.aromiIntegrationOn', 'Is Aromi Integration ON?')
  const worksheetRows = sites.map(site => ({
    [locationNameHeader]: site.locationName,
    [siteNameHeader]: site.siteName,
    [siteIdHeader]: site.siteId,
    [aromiIntegrationHeader]: site.aromiIntegrationOn
  }))
  const worksheet = XLSX.utils.json_to_sheet(worksheetRows)
  const workbook = XLSX.utils.book_new()
  worksheet['!cols'] = [{wch: 24}, {wch: 24}, {wch: 40}, {wch: 28}]

  const headerRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:D1')
  for (let column = headerRange.s.c; column <= headerRange.e.c; column++) {
    const cell = worksheet[XLSX.utils.encode_cell({r: headerRange.s.r, c: column})]
    if (cell) cell.s = {font: {bold: true}}
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, t('locations:export.worksheetName', 'Site Details'))
  XLSX.writeFile(workbook, `site_details_${moment().format('YYYYMMDD_HHmmss')}.xlsx`, {cellStyles: true})
}

type RawSiteImportRow = {
  siteName?: string
  region?: string
  address?: string
  postalCode?: string
  city?: string
  state?: string
  country?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  internalId?: string
  description?: string
  actorSuggestions?: string
}

type ValidSiteImportRow = {
  rowNumber: number
  siteName: string
  region?: string
  locale: string
  timeZone: string
  address?: string
  postalCode?: string
  city?: string
  state?: string
  country?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  internalId?: string
  description?: string
  actorSuggestions?: string[]
}

export type InvalidSiteImportRow = {
  rowNumber: number
  siteName?: string
  field: string
  error: string
}

export const parseSiteImportExcel = async (file: File): Promise<RawSiteImportRow[]> => {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, {type: 'array'})

  const sheetName = workbook.SheetNames[0]
  const worksheet = workbook.Sheets[sheetName]

  return XLSX.utils.sheet_to_json<RawSiteImportRow>(worksheet, {
    defval: '',
    raw: false
  })
}

const parseActorSuggestions = (value?: string): string[] => {
  if (!value) return []

  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

const getTz = (locale: string): string[] => {
  const countryCode = locale.split('-')[1]
  const zonesWithOffset = mz.tz.zonesForCountry(countryCode, true)

  return _.sortBy(zonesWithOffset, o => o.offset).map(tz => tz.name)
}

const getDefaultTimeZone = (locale: string): string | undefined => {
  const timeZones = getTz(locale)

  if (!timeZones.length) {
    return undefined
  }

  return timeZones[0]
}

const DEFAULT_LOCALE = 'fi-FI'
const DEFAULT_REGION = 'Finland'

export const validateSiteImportRows = (
  rows: RawSiteImportRow[],
  userLanguage: UserLanguage = UserLanguage.en,
  t: any
) => {
  // @ts-ignore
  const displayName = new Intl.DisplayNames([userLanguage], {type: 'region'})
  const allCountryOptions = allLocales
    .map(locale => ({
      option: displayName.of(locale.split('-')[1]),
      id: locale
    }))
    .sort((lhs, rhs) => (lhs.option || '').localeCompare(rhs.option || ''))

  const findLocaleByRegionName = (region?: string): string | undefined => {
    if (!region?.trim()) return DEFAULT_LOCALE

    const normalizedRegion = region.trim().toLowerCase()

    return allCountryOptions.find(option => {
      return option.option?.trim().toLowerCase() === normalizedRegion
    })?.id
  }

  const validRows: ValidSiteImportRow[] = []
  const invalidRows: InvalidSiteImportRow[] = []

  rows.forEach((row, index) => {
    const rowNumber = index + 2
    const siteName = row.siteName?.trim()

    const region = row.region?.trim() || DEFAULT_REGION
    const locale = findLocaleByRegionName(row.region)
    const timeZone = locale ? getDefaultTimeZone(locale) : undefined

    const rowErrors: InvalidSiteImportRow[] = []

    if (!siteName) {
      rowErrors.push({
        rowNumber,
        siteName,
        field: 'siteName',
        error: t('locations:errors.siteNameRequired', 'Site name is required')
      })
    }

    if (row.region?.trim() && !locale) {
      rowErrors.push({
        rowNumber,
        siteName,
        field: 'region',
        error: t(
          'locations:errors.regionNotSupported',
          'Region "{{region}}" is not supported. Use the same region name shown in Chefstein.',
          {region: row.region}
        )
      })
    }

    if (!timeZone) {
      rowErrors.push({
        rowNumber,
        siteName,
        field: 'timeZone',
        error: t('locations:errors.timezoneNotFound', 'Could not find timezone for region "{{region}}"', {region})
      })
    }

    if (rowErrors.length > 0) {
      invalidRows.push(...rowErrors)
      return
    }

    validRows.push({
      rowNumber,
      siteName: siteName!,
      region,
      locale: locale!,
      timeZone: timeZone!,
      address: row.address?.trim(),
      postalCode: row.postalCode?.trim(),
      city: row.city?.trim(),
      state: row.state?.trim(),
      country: row.country?.trim(),
      contactName: row.contactName?.trim(),
      contactEmail: row.contactEmail?.trim(),
      contactPhone: row.contactPhone?.trim(),
      internalId: row.internalId?.trim(),
      description: row.description?.trim(),
      actorSuggestions: parseActorSuggestions(row.actorSuggestions)
    })
  })

  return {validRows, invalidRows}
}
